import { useEffect, useRef, useState } from 'react';
import {
	getBackgroundColorClassName,
	getFontColorClassName,
} from '../../lib/getColorClassNames';
import { FrontPage } from '../../types/optimizely/frontpage';
import { useStickyNavigationData } from './useStickyNavigationData';
import { CustomDropdown } from '../CustomDropdown/CustomDropdown';
import { BlocksData } from '../../types/optimizely/blocks/stickyNavigation';
import { getStickyButtonOnHoverClassNames } from '../../lib/getStickyButtonOnHoverClassNames';

interface StickyNavigationProps {
	content: FrontPage | null;
}

const checkVisibility = (element: HTMLElement | null) => {
	if (element) {
		const { top, bottom } = element.getBoundingClientRect();
		return top <= 200 && bottom >= 200;
	}
	return false;
};

export const StickyNavigation = ({ content }: StickyNavigationProps) => {
	const [currentElement, setCurrentElement] = useState<
		BlocksData | undefined
	>();
	const navRef = useRef<HTMLDivElement>(null);
	const data = useStickyNavigationData(content);
	const [offsetTop, setOffsetTop] = useState(0);

	const backgroundColorClass = getBackgroundColorClassName(
		data?.backgroundColor,
	);
	const fontColorClass = getFontColorClassName(data?.fontColor);

	const navigateToElement = (id: string) => {
		const element = document.getElementById(id);
		const scrollY = window.scrollY || window.pageYOffset;

		const foundElement = data?.blocksData?.find((block) => block.id === id);

		if (foundElement) {
			setCurrentElement(foundElement);
		}

		if (element && navRef.current) {
			const { height } = navRef.current.getBoundingClientRect();
			const yOffset =
				scrollY + element.getBoundingClientRect().top - offsetTop - height;
			window.scrollTo({ top: yOffset, behavior: 'smooth' });
		}
	};

	const calculateOffset = () => {
		const bocMainNavigation: HTMLDivElement | null =
			document.querySelector('.main-header');

		if (bocMainNavigation?.offsetHeight) {
			bocMainNavigation.offsetHeight;
			const newOffsetTop = bocMainNavigation.offsetHeight;
			setOffsetTop(newOffsetTop);
		}
	};

	const handleScroll = (sections: (HTMLElement | null)[]) => {
		calculateOffset();
		const arrayOfElementsInView =
			sections.map((element) => (element ? checkVisibility(element) : false)) ??
			[];

		const lastTrueIndex = arrayOfElementsInView.lastIndexOf(true);

		if (lastTrueIndex !== -1) {
			setCurrentElement(data?.blocksData[lastTrueIndex]);
		}
	};

	useEffect(() => {
		calculateOffset();

		const sections = data?.blocksData?.map((block) =>
			document.getElementById(block.id),
		);

		document.addEventListener('scroll', () => handleScroll(sections ?? []));
		window.addEventListener('resize', () => {
			calculateOffset();
		});

		return () => {
			document.removeEventListener('scroll', () =>
				handleScroll(sections ?? []),
			);
			window.removeEventListener('resize', () => {
				calculateOffset();
			});
		};
	}, [data?.blocksData, offsetTop]);

	return (
		<nav
			ref={navRef}
			className={`${backgroundColorClass} ${fontColorClass} w-full lg:max-w-[1200px] mx-auto fixed lg:sticky lg:mt-[-72px] lg:transform lg:translate-y-1/2 lg:px-2 lg:py-2 flex justify-center lg:rounded-3xl z-10 shadow-md`}
			style={{ top: `${offsetTop}px` }}
		>
			<ul className="hidden lg:flex flex-row gap-6">
				{data?.blocksData?.map((block) => (
					<li
						key={block.id}
						className={`cursor-pointer rounded-3xl ${backgroundColorClass} px-12 py-2 font-bold ${getStickyButtonOnHoverClassNames(backgroundColorClass)}`}
						onClick={() => navigateToElement(block.id)}
					>
						{block.title}
					</li>
				))}
			</ul>
			{data?.blocksData && (
				<CustomDropdown
					options={data.blocksData}
					currentLabel={currentElement?.title}
					handleOptionSelect={navigateToElement}
					backgroundColorClass={backgroundColorClass}
				/>
			)}
		</nav>
	);

	return null;
};
